import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';

// mui
import Drawer from '@material-ui/core/Drawer';
import { makeStyles } from '@material-ui/core/styles';

import { Store } from 'redux/root';
import { resetError, setError } from 'redux/error-service/action';
import { getVolunteers, resetVolunteers } from 'redux/volunteer-service/actions';

import { INITIAL_PAGINATION } from 'core/constants';
import { parseQueryStringVolunteersSearch } from 'core/functions';

import { VolunteersPageContext, VolunteersPageContextType } from 'pages/Organization/Volunteers';
import VolunteersTable from 'components/Organization/Volunteers/Table';
import VolunteersFilters from 'components/Organization/Volunteers/VolunteersFilters';
import VolunteerTabs from './VolTabs';
import PendingApprovalTable from 'components/Organization/Volunteers/PendingApprovalTable';

import {
	ISearchVolunteersByGroupRequest,
	ISearchVolunteersRequest,
	PaginationRequest,
} from '@joc/api-gateway/lib/api-client';

import { VOLUNTEER_TABS } from './types';

// styles
import styles from './VolListContent.module.scss';
import { usePendingVolunteers } from 'hooks/volunteers/usePendingVolunteers';

const useDrawerStyles = makeStyles({
	paper: {
		zIndex: 99,
	},
});

const VolListContent: FC = () => {
	const dispatch = useDispatch();
	const drawerClasses = useDrawerStyles();
	const [isLoading, setIsLoading] = useState(true);
	const [pagination, setPagination] = useState(INITIAL_PAGINATION);
	const [activeTab, setActiveTab] = useState<VOLUNTEER_TABS>(VOLUNTEER_TABS.ALL);
	const [refreshToken, setRefreshToken] = useState(0);

	usePendingVolunteers('', undefined, 1);

	const location = useLocation();
	const userLocation = useSelector((store: Store) => store.userRedux.userData?.address);
	const [sidebarVisibility, setSidebarVisibility] = useState(false);
	const [isClearSearch, setIsClearSearch] = useState(false);

	const requestBody: ISearchVolunteersRequest | ISearchVolunteersByGroupRequest = useMemo(() => {
		return parseQueryStringVolunteersSearch(location.search, userLocation);
	}, [location.search]);

	const changeVisibilityFilters = useCallback(() => setSidebarVisibility(prev => !prev), []);
	const handleCloseFilters = useCallback(() => setSidebarVisibility(false), []);

	const refreshVolunteers = useCallback(() => {
		dispatch(resetVolunteers());
		setPagination(INITIAL_PAGINATION);
		setRefreshToken(token => token + 1);
	}, [dispatch]);

	const volunteersPageContextValue: VolunteersPageContextType = useMemo(() => {
		return {
			isLoading,
			isClearSearch,
			sort: requestBody?.sort,
			setPagination,
			changeVisibilityFilters,
			setIsClearSearch,
			refreshVolunteers,
		};
	}, [isLoading, isClearSearch, requestBody, changeVisibilityFilters, refreshVolunteers]);

	const getVolunteersRequest = useCallback(
		async (checkedPagination: PaginationRequest) => {
			if (activeTab === VOLUNTEER_TABS.ALL) {
				setIsLoading(true);
				try {
					await dispatch(getVolunteers({ ...requestBody, pagination: checkedPagination }));
					setIsLoading(false);
				} catch (error: any) {
					setIsLoading(false);
					setError(error?.response?.message || error.message, error.code);
				}
			}
		},
		[pagination, requestBody, activeTab, refreshToken]
	);

	useEffect(() => {
		const checkedPagination =
			pagination.take > 0
				? PaginationRequest.fromJS(pagination)
				: PaginationRequest.fromJS({ ...pagination, take: 10 });
		getVolunteersRequest(checkedPagination);
	}, [getVolunteersRequest]);

	useEffect(() => {
		dispatch(resetError());
		if (activeTab === VOLUNTEER_TABS.ALL) {
			dispatch(resetVolunteers());
			setPagination(INITIAL_PAGINATION);
		}
	}, [location.search, activeTab]);

	useEffect(() => {
		setPagination(INITIAL_PAGINATION);

		return () => {
			dispatch(resetVolunteers());
			dispatch(resetError());
		};
	}, []);

	return (
		<div className={styles.content__wrapper}>
			<div className={styles.content}>
				<VolunteerTabs activeTab={activeTab} setActiveTab={setActiveTab} />
				{activeTab === VOLUNTEER_TABS.ALL ? (
					<VolunteersPageContext.Provider value={volunteersPageContextValue}>
						<VolunteersTable />
					</VolunteersPageContext.Provider>
				) : activeTab === VOLUNTEER_TABS.PENDING_APPROVAL ? (
					<PendingApprovalTable />
				) : null}
			</div>
			<Drawer
				anchor="right"
				variant="persistent"
				classes={{ paper: drawerClasses.paper }}
				open={sidebarVisibility}
				onClose={handleCloseFilters}
			>
				<VolunteersFilters
					changeVisibilityFilters={handleCloseFilters}
					isCloseFilters={false}
					setIsClearSearch={setIsClearSearch}
				/>
			</Drawer>
		</div>
	);
};

export default VolListContent;
